import { MoneyMapStatus } from '@fruitful/types';
import { Redirect, useLocalSearchParams } from 'expo-router';
import { useMemo } from 'react';
import {
    BodyMediumRegular,
    CalendarSyncIcon,
    FullScreenSpinner,
    Icon,
    SafeAreaView,
    XStack,
} from 'smoothie';

import { useAuthedUser } from '../../../../common/providers/AuthedUserProvider';
import { MoneyMapRedesign } from '../../../../roadmap/components/money-map/MoneyMapRedesign';
import { RoadmapScreenHeader } from '../../../../roadmap/components/RoadmapScreenHeader';
import { useGetActiveMoneyMapWithIncomeSplit } from '../../../../roadmap/hooks/useGetActiveMoneyMapWithIncomeSplit';
import { useGetMoneyMapsForMembership } from '../../../../roadmap/hooks/useGetMoneyMapsForMembership';
import { getMoneyMapAuthorshipLabel } from '../../../../roadmap/utils/moneyMapAuthorship';
import { getLiveSplitSourceMoneyMapId } from '../../../../roadmap/utils/moneyMapMemberEditEligibility';

const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

const PreviousMoneyMapDetailScreen = () => {
    const user = useAuthedUser();
    const { moneyMapId } = useLocalSearchParams<{ moneyMapId: string }>();

    const { data: moneyMaps, isLoading: isLoadingMoneyMaps } =
        useGetMoneyMapsForMembership({
            status: [MoneyMapStatus.INACTIVE],
        });
    const { data: activeWithSplit, isLoading: isLoadingActiveWithSplit } =
        useGetActiveMoneyMapWithIncomeSplit({});

    const liveSplitMoneyMapId = getLiveSplitSourceMoneyMapId(
        activeWithSplit?.currentIncomeSplit ?? null,
    );

    const moneyMap = useMemo(
        () =>
            moneyMaps?.find(
                (m) => m.id === moneyMapId && m.id !== liveSplitMoneyMapId,
            ),
        [moneyMaps, moneyMapId, liveSplitMoneyMapId],
    );

    if (isLoadingMoneyMaps || isLoadingActiveWithSplit) {
        return <FullScreenSpinner />;
    }

    if (moneyMap == null) {
        return <Redirect href="/roadmap/previous-money-maps" />;
    }

    const headerTitle = formatDate(moneyMap.createdAt);
    const archivedDate =
        moneyMap.updatedAt != null ? formatDate(moneyMap.updatedAt) : null;

    return (
        <SafeAreaView edges={['left', 'right', 'bottom']}>
            <RoadmapScreenHeader
                title={headerTitle}
                subtitle={getMoneyMapAuthorshipLabel(moneyMap, user)}
                hasBackButton={true}
            />
            <MoneyMapRedesign
                moneyMap={moneyMap}
                mode="archive-view"
                contentAboveVisual={
                    archivedDate != null ? (
                        <XStack
                            marginTop={12}
                            backgroundColor="$gray200"
                            borderRadius={100}
                            paddingVertical={12}
                            paddingHorizontal={20}
                            alignItems="center"
                            gap={10}
                            alignSelf="center"
                        >
                            <Icon icon={CalendarSyncIcon} size={18} />
                            <BodyMediumRegular fontStyle="italic">
                                Archived on {archivedDate}
                            </BodyMediumRegular>
                        </XStack>
                    ) : undefined
                }
            />
        </SafeAreaView>
    );
};

export default PreviousMoneyMapDetailScreen;
